/**
 * Delivery service - validates postcodes and calculates delivery pricing.
 * Uses backend POST /api/v1/delivery/quote (abbey_postal rules + DHL mapping).
 */

import { API_BASE_URL } from 'config';

export interface DeliveryPricingItems {
  weight?: number;
  quantity: number;
  /** Product name or description — used to exclude 25kg products from free delivery */
  description?: string;
}

export interface GetDeliveryPricingRequest {
  postcode: string;
  items?: DeliveryPricingItems[];
  country?: string;
  /** Goods total (net, ex VAT) used to determine free shipping eligibility */
  orderTotal?: number;
  /** Currency ID: 1 = GBP, 2 = EUR. Thresholds are admin-configured (see getFreeShippingThresholds). */
  currencyId?: number;
}

export interface FreeShippingThresholds {
  /** Free-shipping goods-net (ex VAT) threshold in GBP */
  freeShippingThresholdGbp: number;
  /** Free-shipping goods-net (ex VAT) threshold in EUR */
  freeShippingThresholdEur: number;
}

/** Fallback used only if the thresholds endpoint is unreachable. */
const DEFAULT_FREE_SHIPPING_THRESHOLDS: FreeShippingThresholds = {
  freeShippingThresholdGbp: 160,
  freeShippingThresholdEur: 185,
};

export type DeliveryServiceTier = 'standard' | 'pre12' | 'pre1030';

export interface DeliveryOption {
  carrier: string;
  deliveryMethodId: number;
  price: number;
  name: string;
  serviceTier: DeliveryServiceTier;
  /** When true, importer EORI and VAT are required for DPD (Ireland, Channel Islands, Isle of Man, Northern Ireland) */
  requiresCustomsData?: boolean;
  description?: string;
}

export interface DeliveryPricingResponse {
  success: boolean;
  data?: {
    postcode?: string;
    postcodeInfo?: {
      zone: string;
    };
    pricing?: {
      zone: string;
      parcelCount: number;
      ratePerParcel: number;
      totalCost: number;
    };
    price?: number;
    currency?: 'GBP' | 'EUR';
    zone?: string;
    parcelCount?: number;
    ratePerParcel?: number;
    deliveryMethodId?: number;
    timedDeliveryEligible?: boolean;
    options?: DeliveryOption[];
    freeShipping?: boolean;
    dpdLightweight?: boolean;
  };
  error?: string;
  message?: string;
}

function calculateTotalWeightKg(items: DeliveryPricingItems[]): number {
  if (!items?.length) return 0;
  let total = 0;
  for (const item of items) {
    total += (item?.weight ?? 0) * (item?.quantity ?? 1);
  }
  return total;
}

/** Build the POST /delivery/quote request body shared by storefront and admin callers. */
export function buildDeliveryQuoteBody(request: GetDeliveryPricingRequest): Record<string, unknown> {
  const { postcode, items = [], country = 'GB', orderTotal, currencyId } = request;
  const trimmed = postcode?.trim() ?? '';
  const totalWeightKg = calculateTotalWeightKg(items);
  const countryCode = country === 'IE' || country === 'Ireland' ? 'IE' : 'GB';

  const body: Record<string, unknown> = {
    postcode: trimmed,
    country: countryCode,
  };
  if (totalWeightKg > 0) {
    body.totalWeightKg = totalWeightKg;
  } else if (items.length > 0) {
    body.parcelCount = 1;
  }
  if (orderTotal != null) {
    body.orderTotal = orderTotal;
  }
  if (currencyId != null) {
    body.currencyId = currencyId;
  }
  if (items.length > 0) {
    body.items = items.map((item) => ({
      weight: item.weight ?? 0,
      quantity: item.quantity,
      ...(item.description?.trim() ? { description: item.description.trim() } : {}),
    }));
  }
  return body;
}

/** Normalise a /delivery/quote fetch Response into DeliveryPricingResponse. */
export async function parseDeliveryQuoteResponse(res: Response): Promise<DeliveryPricingResponse> {
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return {
      success: false,
      error: 'Delivery service unavailable',
      message: 'Failed to calculate delivery pricing. Please try again.',
    };
  }

  const json = await res.json();

  if (!res.ok) {
    const errMsg =
      json?.message ||
      json?.error ||
      (res.status === 400 ? 'Invalid postcode or delivery not available' : 'Delivery quote failed');
    return { success: false, error: errMsg, message: errMsg };
  }

  if (!json?.success || !json?.data) {
    return { success: false, error: 'Invalid response', message: 'Failed to get delivery quote' };
  }

  const d = json.data;
  return {
    success: true,
    data: {
      postcode: d.postcode,
      postcodeInfo: d.postcodeInfo ? { zone: d.postcodeInfo.zone } : { zone: d.zone || '' },
      pricing: {
        zone: d.zone || 'mainland',
        parcelCount: d.parcelCount ?? 1,
        ratePerParcel: d.ratePerParcel ?? d.price ?? 0,
        totalCost: d.price ?? 0,
      },
      price: d.price,
      currency: d.currency,
      zone: d.zone,
      parcelCount: d.parcelCount,
      ratePerParcel: d.ratePerParcel,
      deliveryMethodId: d.deliveryMethodId,
      timedDeliveryEligible: d.timedDeliveryEligible,
      options: d.options,
      freeShipping: d.freeShipping,
      dpdLightweight: d.dpdLightweight,
    },
  };
}

class DeliveryService {
  /**
   * Get delivery pricing for a postcode.
   * Calls backend POST /api/v1/delivery/quote — cart lines in body to avoid URL length limits.
   */
  async getDeliveryPricing(request: GetDeliveryPricingRequest): Promise<DeliveryPricingResponse> {
    const trimmed = request.postcode?.trim();
    if (!trimmed) {
      return { success: false, error: 'Postcode is required', message: 'Postcode is required' };
    }

    try {
      const body = buildDeliveryQuoteBody(request);
      const res = await fetch(`${API_BASE_URL}/api/v1/delivery/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return await parseDeliveryQuoteResponse(res);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get delivery quote';
      return {
        success: false,
        error: message,
        message: 'Failed to calculate delivery pricing. Please try again.',
      };
    }
  }

  /**
   * Current free-shipping thresholds (admin-configured) for display, e.g. the
   * site header banner. Falls back to defaults if the endpoint is unreachable.
   */
  async getFreeShippingThresholds(): Promise<FreeShippingThresholds> {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/delivery/free-shipping-thresholds`);
      if (!res.ok) return DEFAULT_FREE_SHIPPING_THRESHOLDS;
      const json = await res.json();
      if (!json?.success || !json?.data) return DEFAULT_FREE_SHIPPING_THRESHOLDS;
      return {
        freeShippingThresholdGbp:
          Number(json.data.freeShippingThresholdGbp) ||
          DEFAULT_FREE_SHIPPING_THRESHOLDS.freeShippingThresholdGbp,
        freeShippingThresholdEur:
          Number(json.data.freeShippingThresholdEur) ||
          DEFAULT_FREE_SHIPPING_THRESHOLDS.freeShippingThresholdEur,
      };
    } catch {
      return DEFAULT_FREE_SHIPPING_THRESHOLDS;
    }
  }
}

export const deliveryService = new DeliveryService();
